import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusCircle, Users, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogTrigger } from '@/components/ui/dialog';
import TeamCard from '@/components/teams/TeamCard';
import TeamForm from '@/components/teams/TeamForm';
import ManageTeamMembersDialog from '@/components/teams/ManageTeamMembersDialog';
import { supabase } from '@/lib/supabaseClient';
import { useToast } from '@/components/ui/use-toast';

const TeamsPage = () => {
  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isTeamFormOpen, setIsTeamFormOpen] = useState(false);
  const [isManageMembersOpen, setIsManageMembersOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const { toast } = useToast();

  const fetchTeams = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data: teamsData, error: teamsError } = await supabase
        .from('teams')
        .select(`
          id,
          name,
          department,
          created_at,
          employees (id, name, photo_url)
        `)
        .order('name', { ascending: true });

      if (teamsError) throw teamsError;
      setTeams(teamsData || []);
    } catch (err) {
      console.error('Error fetching teams:', err);
      setError('Failed to load teams. Please try again.');
      toast({
        title: 'Error',
        description: 'Could not fetch teams.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  const handleOpenTeamForm = (team = null) => {
    setSelectedTeam(team);
    setIsTeamFormOpen(true);
  };

  const handleCloseTeamForm = () => {
    setSelectedTeam(null);
    setIsTeamFormOpen(false);
  };

  const handleOpenManageMembers = (team) => {
    setSelectedTeam(team);
    setIsManageMembersOpen(true);
  };

  const handleCloseManageMembers = () => {
    setSelectedTeam(null);
    setIsManageMembersOpen(false);
    fetchTeams(); // Refresh teams data after managing members
  };

  const handleSaveTeam = async (teamData) => {
    try {
      let response;
      if (selectedTeam && selectedTeam.id) {
        response = await supabase
          .from('teams')
          .update(teamData)
          .eq('id', selectedTeam.id)
          .select()
          .single();
      } else {
        response = await supabase
          .from('teams')
          .insert(teamData)
          .select()
          .single();
      }

      const { error: saveError } = response;
      if (saveError) throw saveError;

      toast({
        title: 'Success!',
        description: `Team ${selectedTeam ? 'updated' : 'created'} successfully.`,
        className: 'bg-green-600 text-white',
      });
      fetchTeams();
      handleCloseTeamForm();
    } catch (err) {
      console.error('Error saving team:', err);
      toast({
        title: 'Error saving team',
        description: err.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    }
  };
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <div className="space-y-8">
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex justify-between items-center"
      >
        <h1 className="text-3xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">
          Team Management
        </h1>
        <Dialog open={isTeamFormOpen} onOpenChange={setIsTeamFormOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => handleOpenTeamForm()} className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white">
              <PlusCircle size={20} className="mr-2" /> Add New Team
            </Button>
          </DialogTrigger>
          {isTeamFormOpen && (
            <TeamForm
              team={selectedTeam}
              onSave={handleSaveTeam}
              onCancel={handleCloseTeamForm}
            />
          )}
        </Dialog>
      </motion.div>

      {isLoading && (
        <div className="flex justify-center items-center h-[calc(100vh-300px)]">
          <Loader2 className="h-12 w-12 animate-spin text-purple-400" />
          <p className="ml-4 text-xl text-slate-300">Loading teams...</p>
        </div>
      )}

      {!isLoading && error && (
        <div className="flex flex-col items-center justify-center h-[calc(100vh-300px)] text-red-400 bg-red-500/10 p-6 rounded-lg">
          <AlertTriangle size={48} className="mb-4" />
          <p className="text-xl font-semibold">{error}</p>
        </div>
      )}

      {!isLoading && !error && teams.length === 0 && (
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center justify-center h-[calc(100vh-300px)] text-slate-500"
        >
          <Users className="w-24 h-24 mb-6 text-purple-500/30" />
          <h2 className="text-2xl font-semibold mb-2 text-slate-300">No Teams Yet</h2>
          <p className="text-slate-400">Click "Add New Team" to get started.</p>
        </motion.div>
      )}

      {!isLoading && !error && teams.length > 0 && (
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          <AnimatePresence>
            {teams.map((team) => (
              <TeamCard 
                key={team.id} 
                team={team} 
                onEdit={() => handleOpenTeamForm(team)}
                onManageMembers={() => handleOpenManageMembers(team)}
              />
            ))}
          </AnimatePresence>
        </motion.div>
      )}

      {/* Dialog for Managing Team Members */}
      <Dialog open={isManageMembersOpen} onOpenChange={setIsManageMembersOpen}>
        {selectedTeam && isManageMembersOpen && (
          <ManageTeamMembersDialog
            team={selectedTeam}
            onSave={handleCloseManageMembers} // Refreshes team list on save
            onCancel={() => {
              setIsManageMembersOpen(false);
              setSelectedTeam(null);
            }}
          />
        )}
      </Dialog>
    </div>
  );
};

export default TeamsPage;